'use client';

import React, { useState } from 'react';
import { toast } from 'sonner';
import StatusBadge from '@/components/ui/StatusBadge';
import EmptyState from '@/components/ui/EmptyState';
import { FlockRecord } from './flockData';
import {
  ArrowUpDown, ArrowUp, ArrowDown,
  Eye, Edit2, Trash2, MoreHorizontal,
  Layers, CheckSquare, Square,
} from 'lucide-react';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

interface Props {
  flocks: FlockRecord[];
  selectedIds: string[];
  onSelectIds: (ids: string[]) => void;
  onView: (f: FlockRecord) => void;
  sortCol: keyof FlockRecord;
  sortDir: 'asc' | 'desc';
  onSort: (col: keyof FlockRecord) => void;
  allFlocks: FlockRecord[];
  onFlockUpdate: (f: FlockRecord) => void;
  onFlockDelete: (id: string) => void;
}

type Column = {
  key: keyof FlockRecord;
  label: string;
  sortable?: boolean;
  width?: string;
  align?: 'left' | 'right' | 'center';
};

const COLUMNS: Column[] = [
  { key: 'flockNumber', label: 'Flock #', sortable: true, width: '120px' },
  { key: 'flockName', label: 'Name / Breed', sortable: true, width: '180px' },
  { key: 'birdType', label: 'Type', sortable: true, width: '90px', align: 'center' },
  { key: 'farm', label: 'Farm', sortable: true, width: '160px' },
  { key: 'house', label: 'House', sortable: true, width: '140px' },
  { key: 'arrivalDate', label: 'Arrival', sortable: true, width: '100px' },
  { key: 'currentQty', label: 'Current Birds', sortable: true, width: '110px', align: 'right' },
  { key: 'ageDays', label: 'Age (days)', sortable: true, width: '90px', align: 'right' },
  { key: 'mortalityPct', label: 'Mortality %', sortable: true, width: '100px', align: 'right' },
  { key: 'fcr', label: 'FCR', sortable: true, width: '70px', align: 'right' },
  { key: 'avgWeightKg', label: 'Avg Wt (kg)', sortable: true, width: '100px', align: 'right' },
  { key: 'status', label: 'Status', sortable: true, width: '100px', align: 'center' },
];

function SortIcon({ col, sortCol, sortDir }: { col: keyof FlockRecord; sortCol: keyof FlockRecord; sortDir: 'asc' | 'desc' }) {
  if (col !== sortCol) return <ArrowUpDown size={12} className="text-muted-foreground opacity-40" />;
  return sortDir === 'asc' ? <ArrowUp size={12} style={{ color: 'var(--primary)' }} /> : <ArrowDown size={12} style={{ color: 'var(--primary)' }} />;
}

function FCRBadge({ fcr }: { fcr: number }) {
  if (fcr === 0) return <span className="text-muted-foreground text-xs">—</span>;
  const good = fcr <= 1.8;
  const warn = fcr > 1.8 && fcr <= 2.1;
  return (
    <span
      className="text-xs font-semibold font-tabular px-1.5 py-0.5 rounded"
      style={{
        backgroundColor: good ? 'var(--success-bg)' : warn ? 'var(--warning-bg)' : 'var(--danger-bg)',
        color: good ? 'var(--success)' : warn ? 'var(--warning)' : 'var(--danger)',
      }}
    >
      {fcr.toFixed(2)}
    </span>
  );
}

function MortalityCell({ pct }: { pct: number }) {
  const color = pct > 5 ? 'var(--danger)' : pct > 3 ? 'var(--warning)' : 'var(--success)';
  return (
    <span className="text-xs font-semibold font-tabular" style={{ color }}>
      {pct.toFixed(2)}%
    </span>
  );
}

export default function FlockDataTable({ flocks, selectedIds, onSelectIds, onView, sortCol, sortDir, onSort, onFlockDelete }: Props) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [actionMenuId, setActionMenuId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const totalPages = Math.ceil(flocks.length / pageSize);
  const paginated = flocks.slice((page - 1) * pageSize, page * pageSize);

  const allPageSelected = paginated.length > 0 && paginated.every((f) => selectedIds.includes(f.id));

  function toggleAll() {
    if (allPageSelected) {
      onSelectIds(selectedIds.filter((id) => !paginated.find((f) => f.id === id)));
    } else {
      onSelectIds([...new Set([...selectedIds, ...paginated.map((f) => f.id)])]);
    }
  }

  function toggleOne(id: string) {
    onSelectIds(selectedIds.includes(id) ? selectedIds.filter((i) => i !== id) : [...selectedIds, id]);
  }

  function handleDeleteConfirm(id: string) {
    onFlockDelete(id);
    setDeleteConfirmId(null);
    setActionMenuId(null);
  }

  if (flocks.length === 0) {
    return (
      <div className="panel-card">
        <EmptyState
          icon={Layers}
          title="No flocks found"
          description="No flocks match your current filters. Adjust the search or filters, or create a new flock batch."
          action={{ label: 'Clear filters', onClick: () => {} }}
        />
      </div>
    );
  }

  return (
    <div className="panel-card">
      {/* Table */}
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full" style={{ minWidth: '1200px' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>
              <th className="w-10 px-4 py-3">
                <button onClick={toggleAll} className="flex items-center justify-center">
                  {allPageSelected
                    ? <CheckSquare size={15} style={{ color: 'var(--primary)' }} />
                    : <Square size={15} className="text-muted-foreground" />
                  }
                </button>
              </th>
              {COLUMNS.map((col) => (
                <th
                  key={`th-${col.key}`}
                  className="px-3 py-3 text-left"
                  style={{ width: col.width, textAlign: col.align ?? 'left' }}
                >
                  {col.sortable ? (
                    <button
                      onClick={() => onSort(col.key)}
                      className="flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
                      style={{ letterSpacing: '0.02em', justifyContent: col.align === 'right' ? 'flex-end' : col.align === 'center' ? 'center' : 'flex-start' }}
                    >
                      {col.label}
                      <SortIcon col={col.key} sortCol={sortCol} sortDir={sortDir} />
                    </button>
                  ) : (
                    <span className="text-xs font-semibold text-muted-foreground" style={{ letterSpacing: '0.02em' }}>{col.label}</span>
                  )}
                </th>
              ))}
              <th className="px-3 py-3 w-20">
                <span className="text-xs font-semibold text-muted-foreground" style={{ letterSpacing: '0.02em' }}>Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((flock) => {
              const isSelected = selectedIds.includes(flock.id);
              return (
                <tr
                  key={flock.id}
                  className="table-row-hover border-b transition-colors"
                  style={{
                    borderColor: 'var(--border)',
                    backgroundColor: isSelected ? '#f0fdf4' : undefined,
                  }}
                >
                  <td className="px-4 py-3">
                    <button onClick={() => toggleOne(flock.id)} className="flex items-center justify-center">
                      {isSelected
                        ? <CheckSquare size={15} style={{ color: 'var(--primary)' }} />
                        : <Square size={15} className="text-muted-foreground" />
                      }
                    </button>
                  </td>

                  {/* Flock # */}
                  <td className="px-3 py-3">
                    <button
                      onClick={() => onView(flock)}
                      className="text-sm font-semibold font-mono transition-colors hover:underline"
                      style={{ color: 'var(--primary)', fontSize: '0.8125rem' }}
                    >
                      {flock.flockNumber}
                    </button>
                  </td>

                  {/* Name */}
                  <td className="px-3 py-3">
                    <div>
                      <p className="text-sm font-medium text-foreground leading-tight truncate" style={{ maxWidth: '160px' }}>
                        {flock.flockName}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">{flock.breed}</p>
                    </div>
                  </td>

                  {/* Bird Type */}
                  <td className="px-3 py-3 text-center">
                    <StatusBadge status={flock.birdType.toLowerCase().replace('/', '-').replace(' ', '-')} size="sm" />
                  </td>

                  {/* Farm */}
                  <td className="px-3 py-3">
                    <p className="text-xs text-foreground truncate" style={{ maxWidth: '150px' }}>{flock.farm}</p>
                  </td>

                  {/* House */}
                  <td className="px-3 py-3">
                    <p className="text-xs font-medium text-foreground">{flock.house}</p>
                  </td>

                  {/* Arrival */}
                  <td className="px-3 py-3">
                    <p className="text-xs text-foreground font-tabular">{flock.arrivalDate}</p>
                  </td>

                  {/* Current Birds */}
                  <td className="px-3 py-3 text-right">
                    <p className="text-sm font-semibold font-tabular text-foreground">{flock.currentQty.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground font-tabular">of {flock.initialQty.toLocaleString()}</p>
                  </td>

                  {/* Age */}
                  <td className="px-3 py-3 text-right">
                    <span className="text-sm font-tabular text-foreground">{flock.ageDays}d</span>
                  </td>

                  {/* Mortality % */}
                  <td className="px-3 py-3 text-right">
                    <MortalityCell pct={flock.mortalityPct} />
                  </td>

                  {/* FCR */}
                  <td className="px-3 py-3 text-right">
                    <FCRBadge fcr={flock.fcr} />
                  </td>

                  {/* Avg Weight */}
                  <td className="px-3 py-3 text-right">
                    <span className="text-sm font-tabular text-foreground">
                      {flock.avgWeightKg > 0 ? `${flock.avgWeightKg.toFixed(2)} kg` : '—'}
                    </span>
                  </td>

                  {/* Status */}
                  <td className="px-3 py-3 text-center">
                    <StatusBadge status={flock.status} size="sm" dot />
                  </td>

                  {/* Actions */}
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1 justify-end relative">
                      <button
                        onClick={() => onView(flock)}
                        className="btn-ghost p-1.5 group relative"
                        aria-label="View flock details"
                      >
                        <Eye size={14} />
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-1.5 py-0.5 text-xs bg-foreground text-background rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                          View details
                        </span>
                      </button>
                      <div className="relative">
                        <button
                          onClick={() => setActionMenuId(actionMenuId === flock.id ? null : flock.id)}
                          className="btn-ghost p-1.5"
                          aria-label="More actions"
                        >
                          <MoreHorizontal size={14} />
                        </button>
                        {actionMenuId === flock.id && (
                          <div
                            className="absolute right-0 top-full mt-1 w-36 bg-card rounded-lg border shadow-modal py-1 z-20 fade-in"
                            style={{ borderColor: 'var(--border)' }}
                          >
                            <button
                              onClick={() => { onView(flock); setActionMenuId(null); }}
                              className="flex items-center gap-2 px-3 py-2 text-xs text-foreground hover:bg-muted w-full transition-colors"
                            >
                              <Eye size={12} /> View Details
                            </button>
                            <button
                              onClick={() => { toast.info('Edit flock — form coming soon'); setActionMenuId(null); }}
                              className="flex items-center gap-2 px-3 py-2 text-xs text-foreground hover:bg-muted w-full transition-colors"
                            >
                              <Edit2 size={12} /> Edit Flock
                            </button>
                            <div className="border-t my-1" style={{ borderColor: 'var(--border)' }} />
                            <button
                              onClick={() => setDeleteConfirmId(flock.id)}
                              className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted w-full transition-colors"
                              style={{ color: 'var(--danger)' }}
                            >
                              <Trash2 size={12} /> Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Delete confirm inline */}
                    {deleteConfirmId === flock.id && (
                      <div
                        className="absolute right-4 mt-1 w-56 bg-card rounded-lg border shadow-modal p-3 z-30 fade-in"
                        style={{ borderColor: 'var(--danger)' }}
                      >
                        <p className="text-xs font-medium text-foreground mb-2">Delete {flock.flockNumber}?</p>
                        <p className="text-xs text-muted-foreground mb-3">This action cannot be undone. All related records will be preserved in audit logs.</p>
                        <div className="flex items-center gap-2">
                          <button onClick={() => setDeleteConfirmId(null)} className="btn-secondary text-xs py-1 px-2 flex-1">Cancel</button>
                          <button onClick={() => handleDeleteConfirm(flock.id)} className="text-xs font-medium py-1 px-2 rounded flex-1 text-white" style={{ backgroundColor: 'var(--danger)' }}>Delete</button>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t" style={{ borderColor: 'var(--border)' }}>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Rows per page:</span>
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="select-field py-1 px-2 text-xs"
            style={{ height: '28px', width: '60px' }}
          >
            {PAGE_SIZE_OPTIONS.map((s) => (
              <option key={`ps-${s}`} value={s}>{s}</option>
            ))}
          </select>
          <span>
            Showing {Math.min((page - 1) * pageSize + 1, flocks.length)}–{Math.min(page * pageSize, flocks.length)} of {flocks.length} flocks
          </span>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage(1)}
            disabled={page === 1}
            className="btn-ghost px-2 py-1 text-xs disabled:opacity-40"
          >
            «
          </button>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="btn-ghost px-2 py-1 text-xs disabled:opacity-40"
          >
            ‹
          </button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const start = Math.max(1, Math.min(page - 2, totalPages - 4));
            const pageNum = start + i;
            if (pageNum > totalPages) return null;
            return (
              <button
                key={`page-btn-${pageNum}`}
                onClick={() => setPage(pageNum)}
                className="px-2.5 py-1 text-xs rounded font-medium transition-colors"
                style={{
                  backgroundColor: page === pageNum ? 'var(--primary)' : 'transparent',
                  color: page === pageNum ? '#fff' : 'var(--foreground)',
                }}
              >
                {pageNum}
              </button>
            );
          })}
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="btn-ghost px-2 py-1 text-xs disabled:opacity-40"
          >
            ›
          </button>
          <button
            onClick={() => setPage(totalPages)}
            disabled={page === totalPages}
            className="btn-ghost px-2 py-1 text-xs disabled:opacity-40"
          >
            »
          </button>
        </div>
      </div>
    </div>
  );
}